import { createElement as rc, useRef, useEffect, useState } from 'react';
import { Card, Modal, webOnlyStyles, h2, h3, Button, styled, View, fromTheme, getQueue, Icon } from 'lib_ui-primitives';
import { useTheme } from 'styled-components';
import useEventSink from '../../hooks/useEventSink';
import DropDown from '../_abstractComponent/DropDown';
import { GestureRecognizer, ThemeComponentSwitcher } from 'lib_ui-primitives';

import Gl2025ConfirmationDialog from './ConfirmationDialog.gl2025';

const ButtonBar = styled(View)`
    flex-basis: auto;
    flex-grow: 0;
    flex-shrink: 1;
    flex-direction: row;
    justify-content: flex-end;
    min-height: 32px;
    margin: 6px;
    width: 100%;
`;

const ConfirmationCard = webOnlyStyles(Card)`
    box-shadow: unset;
`;

const HeaderText = styled(h2)`
    color: ${fromTheme('card', 'header', 'textColor')};
`;

const Body = styled(Card.Body)`
    flex-direction: row;
    align-items: center;
`;

let DialogIcon = styled(Icon)`
    margin-left: 12px;
    margin-right: 24px;
    font-size: 42px;
`;
DialogIcon = webOnlyStyles(DialogIcon)`
    max-width: unset;
`;

const TopToBottom = styled(View)`
    flex-direction: column;
`;

let BigText = styled(h3)``;
BigText = webOnlyStyles(BigText)`
    margin-block-start: 0;
    margin-block-end: 0;
`;

const defaultDialogInfo = {
    message: '',
    okButtonText: 'OK',
    cancelButtonText: 'CANCEL',
    okAction: () => {},
    cancelAction: () => {},
    noCancelOption: false,
    jumpQueue: false
};

function ConfirmationDialog() {
    const [subscribe] = useEventSink();
    const [dialogInfo, setDialogInfo] = useState(defaultDialogInfo);
    const theme = useTheme();
    const [visible, setVisible] = useState(false);
    const [selection, setSelection] = useState();
    // If more than one confirmation is requested, then queue them.
    // Note: this is a separate queue from the modal queue. This one only controls the content displayed INSIDE the confirmation dialog
    const queue = useRef(getQueue('ConfirmationDialog')).current;

    // When a new confirmation reaches the front of the queue, display it.
    useEffect(() => {
        queue.onNewLeader(newLeader => {
            if (newLeader != null) {
                setDialogInfo(newLeader);
                setSelection(undefined);
                setVisible(true);
            }
        });
    }, [queue]);

    // Listen for confirmation dialog requests and queue them
    useEffect(() => {
        let openActionType = { verb: 'confirm', namespace: 'application', relation: 'user' };
        const unsubscribes = [
            subscribe(openActionType, payload => {
                const dialogInfo = { ...defaultDialogInfo, ...payload };
                if (dialogInfo.jumpQueue) {
                    queue.jumpQueue(dialogInfo);
                } else {
                    queue.enqueue(dialogInfo);
                }
            })
        ];
        // unsubscribe during cleanup
        return () =>
            unsubscribes.forEach(u => {
                u();
            });
    }, [subscribe, queue]);

    const {
        title: defaultTitle,
        message,
        choices,
        choiceLabel = 'Select an option',
        okButtonText,
        cancelButtonText,
        okAction,
        cancelAction,
        noCancelOption,
        icon,
        iconFont,
        iconVariation,
        iconColor,
        iconColorStyle,
        jumpQueue = false
    } = dialogInfo;

    const title = defaultTitle ?? (noCancelOption ? 'Please Be Advised' : 'Please Confirm');

    function onCancel() {
        setVisible(false);
        setDialogInfo(defaultDialogInfo);
        cancelAction();
        queue.next();
    }
    function onOk() {
        if (choices && !choices.some(choice => choice._id === selection?._id)) return;
        setVisible(false);
        setDialogInfo(defaultDialogInfo);
        if (choices) okAction(selection);
        else okAction();
        queue.next();
    }

    // Some confirmations require a choice supplied by the caller. These records
    // are local to this dialog; opening the next confirmation clears the selection.
    // Dialogs sit outside the page gesture provider required by dropdown lists.
    const choice = choices && rc(GestureRecognizer, null,
        rc(DropDown, {
            hNode: {
                id: 'confirmation-choice',
                title: choiceLabel,
                propertyName: 'title',
                inputDisplayProperties: ['title'],
                dropdownDisplayProperties: ['title'],
                treePosition: { sequence: 0 },
                required: true
            },
            records: choices,
            value: selection,
            setValue: setSelection,
            active: visible
        })
    );
    const choiceRequired = choices && !choices.some(item => item._id === selection?._id);

    let messageAsArray = [].concat(message);
    let mlmHtml = messageAsArray.map((line, i) => rc(BigText, { key: i }, line));

    // if we have a color passed in, use it. Otherwise, if there is a color style,
    // use that to look up the color in the theme
    const iconFontColor = iconColor || theme.icon?.[iconColorStyle];

    // prettier-ignore
    return rc(Modal, { id: 'confirmationDialogModal', visible, jumpQueue },
        rc(ConfirmationCard, null,
            rc(Card.Header, null,
                rc(HeaderText, null, title)
            ),
            rc(Body, null,
                icon && rc(DialogIcon,
                    {
                        iconFont,
                        iconVariation,
                        fontColor: iconFontColor,
                        key: '0',
                        title: icon,
                        iconSize: '48px'
                    },
                    icon
                ),
                rc(TopToBottom, null, mlmHtml, choice)
            ),
            rc(Card.Footer, null, rc(ButtonBar, null,
                !noCancelOption && rc(Button, { value: cancelButtonText, onClick: onCancel }),
                rc(Button, { value: okButtonText, onClick: onOk, disabled: choiceRequired })
            ))
        )
    );
}

let SwitchedComponent = ThemeComponentSwitcher([
    {
        theme: 'gl2025',
        component: Gl2025ConfirmationDialog
    },
    { component: ConfirmationDialog }
]);

export default SwitchedComponent;
